import React, { useState } from 'react';
import {
	Modal,
	Form,
	Input,
	Select,
	Space,
	Upload,
	message,
	Checkbox,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { s3Api } from '../../api/s3';
import { useAuth } from '../../context/AuthContext';
import './FolderCreateModal.css';

const { Option } = Select;

function FolderCreateModal({
	visible,
	onClose,
	onSubmit,
	folders,
	tags,
	currentFolderId = null,
	loading = false,
	folderSize = 0,
	requireAcknowledgment = false,
	onAcknowledgmentChange = () => {},
	acknowledged = true,
}) {
	const [form] = Form.useForm();
	const [imageUrl, setImageUrl] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const { orgData, checkStorageLimit } = useAuth();

	const handleSubmit = async (values) => {
		try {
			setUploading(true);

			// Check storage limit for cover photo if selected
			if (selectedFile) {
				const storageCheck = await checkStorageLimit(selectedFile.size);
				if (!storageCheck.canUpload) {
					message.error(storageCheck.message);
					setUploading(false);
					return;
				}
			}

			// If we're creating a folder with contents, check the total size
			if (folderSize > 0) {
				const storageCheck = await checkStorageLimit(folderSize);
				if (!storageCheck.canUpload) {
					message.error(storageCheck.message);
					setUploading(false);
					return;
				}
			}

			let coverPhotoUrl = imageUrl;

			// Upload the image if a new file was selected
			if (selectedFile) {
				const timestamp = Date.now();
				const fileExtension = selectedFile.name
					.split('.')
					.pop()
					.toLowerCase();
				const safeName = `Images/folder-cover-${timestamp}.${fileExtension}`;

				// Upload to S3 using the new s3Api
				coverPhotoUrl = await s3Api.upload(
					orgData.id,
					selectedFile,
					safeName
				);
			}

			// Prepare the submit data
			const submitData = {
				...values,
				coverPhotoUrl,
				// Handle parentFolderId properly
				parentFolderId:
					values.parentFolderId === 'root'
						? null
						: values.parentFolderId,
			};

			// Submit the form data
			await onSubmit(submitData);
			form.resetFields();
			setImageUrl(null);
			setSelectedFile(null);
			onClose();
		} catch (error) {
			console.error('Submit error:', error);
			message.error(
				'Failed to create folder: ' + (error.message || 'Unknown error')
			);
		} finally {
			setUploading(false);
		}
	};

	const beforeUpload = (file) => {
		const isImage = file.type.startsWith('image/');
		if (!isImage) {
			message.error('You can only upload image files!');
			return false;
		}

		const isLt2M = file.size / 1024 / 1024 < 2;
		if (!isLt2M) {
			message.error('Image must be smaller than 2MB!');
			return false;
		}

		return true;
	};

	const handleUpload = async (file) => {
		try {
			// Instead of uploading, we'll create a temporary URL for preview
			const previewUrl = URL.createObjectURL(file);
			setImageUrl(previewUrl);
			setSelectedFile(file);
		} catch (error) {
			console.error('File handling error:', error);
			message.error('Failed to process image');
		}
	};

	// Add storage usage information
	const { storageUsage, storageLimit, isLoadingStorage } = useAuth();

	const renderStorageInfo = () => {
		if (isLoadingStorage) {
			return (
				<div className='storage-info'>Calculating storage usage...</div>
			);
		}

		if (storageUsage && storageLimit) {
			const usagePercent = (storageUsage.gigabytes / storageLimit) * 100;
			return (
				<div className='storage-info'>
					<div>
						Storage: {storageUsage.formattedSize} of {storageLimit}{' '}
						GB used ({usagePercent.toFixed(1)}%)
					</div>
					{folderSize > 0 && (
						<div>
							This folder will use an additional{' '}
							{(folderSize / (1024 * 1024 * 1024)).toFixed(2)} GB
						</div>
					)}
				</div>
			);
		}

		return null;
	};

	const uploadButton = (
		<div>
			<PlusOutlined />
			<div style={{ marginTop: 8 }}>Upload Cover</div>
		</div>
	);

	return (
		<Modal
			title='Create Folder'
			open={visible}
			onCancel={() => {
				form.resetFields();
				setImageUrl(null);
				setSelectedFile(null);
				onClose();
			}}
			okText='Create'
			confirmLoading={loading || uploading}
			onOk={() => form.submit()}
			className='common-modal folder-modal'>
			{renderStorageInfo()}

			<Form
				form={form}
				layout='vertical'
				onFinish={handleSubmit}
				initialValues={{
					parentFolderId: currentFolderId || 'root',
					visibility: 'private',
				}}>
				<Form.Item
					name='coverImage'
					label='Cover Image'>
					<Upload
						name='coverImage'
						listType='picture-card'
						showUploadList={false}
						beforeUpload={beforeUpload}
						customRequest={({ file }) => handleUpload(file)}
						className='cover-image-uploader'>
						{imageUrl ? (
							<img
								src={imageUrl}
								alt='cover'
								style={{
									width: '100%',
									height: '100%',
									objectFit: 'cover',
								}}
							/>
						) : (
							uploadButton
						)}
					</Upload>
				</Form.Item>

				<Form.Item
					name='name'
					label='Name'
					rules={[
						{ required: true, message: 'Please enter a name' },
					]}>
					<Input placeholder='Enter name' />
				</Form.Item>

				<Form.Item
					name='parentFolderId'
					label='Parent Folder'>
					<Select placeholder='Select parent folder'>
						<Option value='root'>Root Folder</Option>
						{folders.length > 0
							? folders.map((folder) => (
									<Option
										key={folder.id}
										value={folder.id}>
										{folder.name}
									</Option>
							  ))
							: null}
					</Select>
				</Form.Item>

				<Form.Item
					name='visibility'
					label='Visibility'
					rules={[
						{ required: true, message: 'Please select visibility' },
					]}>
					<Select placeholder='Select visibility'>
						<Option value='hidden'>Hidden</Option>
						<Option value='private'>Private</Option>
						<Option value='public'>Public</Option>
					</Select>
				</Form.Item>

				<Form.Item
					name='tagId'
					label='Tag'>
					<Select
						allowClear
						placeholder='Select tag'
						optionLabelProp='label'>
						{tags.length > 0
							? tags.map((tag) => (
									<Option
										key={tag.id}
										value={tag.id}
										label={tag.name}>
										<Space>
											<div
												style={{
													width: 16,
													height: 16,
													backgroundColor: tag.color,
													borderRadius: 4,
												}}
											/>
											{tag.name}
										</Space>
									</Option>
							  ))
							: null}
					</Select>
				</Form.Item>

				{requireAcknowledgment && (
					<Form.Item>
						<Checkbox
							checked={acknowledged}
							onChange={(e) =>
								onAcknowledgmentChange(e.target.checked)
							}>
							I acknowledge that this folder is larger than 1GB (
							{(folderSize / (1024 * 1024 * 1024)).toFixed(2)}GB)
						</Checkbox>
					</Form.Item>
				)}
			</Form>
		</Modal>
	);
}

export default FolderCreateModal;
